import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * A/B Testing Context
 *
 * Provides global state management for boolean A/B tests that can be toggled
 * via the DevTools component. Tests are persisted to localStorage.
 *
 * HOW TO ADD NEW TESTS:
 * ---------------------
 * 1. Add a new test definition to the AB_TESTS array below:
 *
 *    {
 *      key: 'my_new_feature',           // Unique identifier (use snake_case)
 *      name: 'My New Feature',          // Display name shown in DevTools
 *      description: 'Enable experimental dashboard redesign',  // Brief description
 *      defaultValue: false,             // Default state (true/false)
 *    }
 *
 * 2. The toggle will automatically appear in the DevTools component
 * 3. Test values persist across page refreshes via localStorage
 *
 * HOW TO USE IN COMPONENTS:
 * -------------------------
 * Import the useABTest hook and call getTestValue() with your test key:
 *
 * Example 1: Conditional rendering
 *
 *   import { useABTest } from '../contexts/ABTestContext';
 *
 *   const MyComponent = () => {
 *     const { getTestValue } = useABTest();
 *     const isEnabled = getTestValue('my_new_feature');
 *
 *     if (!isEnabled) {
 *       return null;  // Feature disabled
 *     }
 *
 *     return <NewFeature />;
 *   };
 *
 * Example 2: Conditional styling/behavior
 *
 *   const MyButton = () => {
 *     const { getTestValue } = useABTest();
 *     const useNewStyle = getTestValue('new_button_style');
 *
 *     return (
 *       <button style={{ color: useNewStyle ? 'orange' : 'blue' }}>
 *         Click me
 *       </button>
 *     );
 *   };
 *
 * Example 3: Multiple tests
 *
 *   const Dashboard = () => {
 *     const { getTestValue } = useABTest();
 *     const showNewChart = getTestValue('new_chart_view');
 *     const enableRealTime = getTestValue('real_time_updates');
 *
 *     return (
 *       <div>
 *         {showNewChart ? <NewChart /> : <OldChart />}
 *         {enableRealTime && <LiveIndicator />}
 *       </div>
 *     );
 *   };
 */

export interface ABTest {
  key: string;
  name: string;
  description: string;
  defaultValue: boolean;
}

interface ABTestContextType {
  tests: ABTest[];
  getTestValue: (key: string) => boolean;
  setTestValue: (key: string, value: boolean) => void;
  resetTests: () => void;
}

const ABTestContext = createContext<ABTestContextType | undefined>(undefined);

/**
 * Define all A/B tests here
 *
 * Each test will automatically appear in the DevTools component with a toggle switch.
 * Add new tests by adding objects to this array following the ABTest interface.
 */
export const AB_TESTS: ABTest[] = [
  {
    key: 'example_feature',
    name: 'Example Feature',
    description: 'Enable the new example feature',
    defaultValue: false,
  },
  // Add more tests here as needed
];

const STORAGE_KEY = 'lgm_ab_tests';

export const ABTestProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [testValues, setTestValues] = useState<Record<string, boolean>>(() => {
    // Initialize from localStorage or defaults
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        // Fall through to defaults
      }
    }
    // Build defaults from test definitions
    return AB_TESTS.reduce(
      (acc, test) => {
        acc[test.key] = test.defaultValue;
        return acc;
      },
      {} as Record<string, boolean>
    );
  });

  // Persist to localStorage whenever values change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(testValues));
  }, [testValues]);

  const getTestValue = (key: string): boolean => {
    return testValues[key] ?? false;
  };

  const setTestValue = (key: string, value: boolean) => {
    setTestValues(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetTests = () => {
    const defaults = AB_TESTS.reduce(
      (acc, test) => {
        acc[test.key] = test.defaultValue;
        return acc;
      },
      {} as Record<string, boolean>
    );
    setTestValues(defaults);
  };

  return (
    <ABTestContext.Provider
      value={{ tests: AB_TESTS, getTestValue, setTestValue, resetTests }}>
      {children}
    </ABTestContext.Provider>
  );
};

export const useABTest = () => {
  const context = useContext(ABTestContext);
  if (!context) {
    throw new Error('useABTest must be used within an ABTestProvider');
  }
  return context;
};
